/**
 * API formateur : saisie des notes de devoirs.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface MesMatieresResp {
  mon_nom: string;
  results: Array<{ classe: string; matiere_libelle: string; matiere_code: string }>;
  total: number;
}

export interface EtuRow {
  matricule: string; nom: string; prenoms: string;
  note1: string | null; absent1: boolean;
  note2: string | null; absent2: boolean;
  moyenne: string | null; verrouille: boolean;
}

export interface SaisieResp {
  classe: string; matiere_libelle: string; matiere_id: number; semestre: number;
  fenetre_ouverte: boolean; etudiants: EtuRow[];
}

export interface Fenetre {
  id: number; libelle: string; type_notes: string; type_label: string;
  semestre: number; annee_libelle: string;
  date_debut: string; date_fin: string;
  ouverte: boolean; active_now: boolean;
  classes_ciblees: string[]; matieres_ciblees: string[]; formateurs_cibles_ids: number[];
  /** Fix 2026-09-01 : noms des formateurs ciblés pour affichage direct. */
  formateurs_cibles_details?: Array<{ id: number; nom_complet: string; actif: boolean }>;
  date_creation: string; cree_par_nom: string;
}

export interface FormateurChoix {
  id: number; nom_complet: string; email: string; username: string;
}

export interface DemandeFenetre {
  id: number;
  formateur_id: number; formateur_nom: string; formateur_email: string;
  type_notes: string; type_label: string;
  semestre: number; annee_libelle: string;
  classe: string; matiere_libelle: string; matiere_code: string;
  motif: string; duree_jours_demandee: number;
  statut: 'en_attente' | 'approuvee' | 'refusee';
  statut_label: string;
  date_demande: string;
  date_traitement: string | null;
  traite_par_nom: string;
  reponse_scolarite: string;
  fenetre_creee_id: number | null;
}

export const formateurNotesApi = createApi({
  reducerPath: 'formateurNotesApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Saisie', 'Fenetres', 'DemandesFenetre'],
  endpoints: (builder) => ({
    mesMatieres: builder.query<MesMatieresResp, void>({
      query: () => '/vie-scolaire/formateur/mes-matieres/',
    }),
    listerSaisie: builder.query<SaisieResp, { classe: string; matiere: string; semestre: number }>({
      query: (p) => ({ url: '/vie-scolaire/formateur/saisie/', params: p }),
      providesTags: ['Saisie'],
    }),
    saveNote: builder.mutation<any, {
      matricule: string; champ: 'note1' | 'note2'; valeur: string | null; absent: boolean;
      classe: string; matiere: string; semestre: number;
    }>({
      query: (body) => ({ url: '/vie-scolaire/formateur/saisie/', method: 'POST', body }),
      invalidatesTags: ['Saisie'],
    }),

    // Fenêtres de saisie (scolarité)
    listerFenetres: builder.query<{ results: Fenetre[] }, void>({
      query: () => '/vie-scolaire/fenetres-saisie/',
      providesTags: ['Fenetres'],
    }),
    creerFenetre: builder.mutation<Fenetre, Partial<Fenetre>>({
      query: (body) => ({ url: '/vie-scolaire/fenetres-saisie/', method: 'POST', body }),
      invalidatesTags: ['Fenetres'],
    }),
    updateFenetre: builder.mutation<Fenetre, { id: number; data: Partial<Fenetre> }>({
      query: ({ id, data }) => ({ url: `/vie-scolaire/fenetres-saisie/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: ['Fenetres'],
    }),
    deleteFenetre: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({ url: `/vie-scolaire/fenetres-saisie/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Fenetres'],
    }),

    // Formateurs (choix pour multi-select modal)
    listerFormateurs: builder.query<{ results: FormateurChoix[]; total: number }, string | void>({
      query: (q) => ({
        url: '/vie-scolaire/formateurs-choix/',
        params: q ? { q } : {},
      }),
    }),

    // Fix 2026-09-08 : matières attribuées à un ou plusieurs formateurs
    matieresDesFormateurs: builder.query<{
      matieres: Array<{
        matiere_id: number; code_matiere: string; nom_matiere: string;
        niveau: string; semestre: number; option: string;
        formateur_user_id: number; formateur_nom: string;
      }>;
      total: number;
    }, number[]>({
      query: (ids) => ({
        url: '/vie-scolaire/matieres-des-formateurs/',
        params: { formateurs_ids: ids.join(',') },
      }),
    }),

    // Demandes d'ouverture de fenêtre (scolarité)
    listerDemandesFenetre: builder.query<
      { results: DemandeFenetre[]; nb_en_attente: number },
      'tous' | 'en_attente' | 'approuvee' | 'refusee' | void
    >({
      query: (statut) => ({
        url: '/vie-scolaire/demandes-fenetre/',
        params: statut && statut !== 'tous' ? { statut } : {},
      }),
      providesTags: ['DemandesFenetre'],
    }),
    traiterDemandeFenetre: builder.mutation<
      DemandeFenetre,
      { id: number; action: 'approuver' | 'refuser'; reponse?: string; duree_jours?: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/vie-scolaire/demandes-fenetre/${id}/traiter/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DemandesFenetre', 'Fenetres'],
    }),

    // Formateur — mes demandes + nouvelle demande
    mesDemandesFenetre: builder.query<
      { results: DemandeFenetre[]; nb_en_attente: number }, void
    >({
      query: () => '/portail-formateur/me/demandes-fenetre/',
      providesTags: ['DemandesFenetre'],
    }),
    demanderFenetre: builder.mutation<DemandeFenetre, {
      classe: string; matiere_libelle: string; matiere_code?: string;
      semestre: number; type_notes?: string;
      motif: string; duree_jours?: number; annee_libelle?: string;
    }>({
      query: (body) => ({
        url: '/portail-formateur/me/demandes-fenetre/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DemandesFenetre'],
    }),
  }),
});

export const {
  useMesMatieresQuery,
  useListerSaisieQuery,
  useSaveNoteMutation,
  useListerFenetresQuery,
  useCreerFenetreMutation,
  useUpdateFenetreMutation,
  useDeleteFenetreMutation,
  useListerFormateursQuery,
  useMatieresDesFormateursQuery,
  useLazyMatieresDesFormateursQuery,
  useListerDemandesFenetreQuery,
  useTraiterDemandeFenetreMutation,
  useMesDemandesFenetreQuery,
  useDemanderFenetreMutation,
} = formateurNotesApi;
